import React, { useEffect, useState } from "react";
import { ArrowLeft, Package, Clock, DollarSign, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import VisitorNavBar from "../../components/Common/VisitorNavBar";
import { useAuth } from "../../contexts/AuthContext";
import { formatPrice } from "../../utils/formatters";

export default function MyOffersPage() {
  const navigate = useNavigate();
  const { user, session } = useAuth() || {};
  const isDev = import.meta?.env?.MODE === "development";

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      setError("You need to be logged in to view your offers.");
      return;
    }

    async function loadOffers() {
      setLoading(true);
      setError("");
      try {
        const token = session?.access_token;
        const res = await fetch("/api/visitor/offers", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (!res.ok || !data.ok) {
          throw new Error(data.error || "Failed to load offers.");
        }
        setOffers(data.offers || []);
      } catch (err) {
        if (isDev) console.warn("[MyOffers] load error", err);
        setError(err.message || "Failed to load offers.");
      } finally {
        setLoading(false);
      }
    }

    loadOffers();
  }, [user?.id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white">
      <VisitorNavBar showBackButton backTo="/discover" />

      <div className="pt-24 pb-12 px-4 max-w-5xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-sm text-white/70"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-2xl font-bold mb-1">My offers</h1>
        <p className="text-sm text-white/60 mb-6">
          Track offers you&apos;ve sent to studios and see their status.
        </p>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="inline-block w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && error && (
          <p className="text-red-400 text-sm mt-4">{error}</p>
        )}

        {!loading && !error && offers.length === 0 && (
          <div className="text-center py-16 text-white/70">
            <Package className="w-12 h-12 mx-auto mb-4 text-white/30" />
            <p className="text-lg mb-1">No offers yet</p>
            <p className="text-sm text-white/50">
              Browse the marketplace and make an offer on a piece you love.
            </p>
          </div>
        )}

        {!loading && !error && offers.length > 0 && (
          <div className="space-y-3 mt-4">
            {offers.map((offer) => (
              <OfferRow key={offer.id} offer={offer} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OfferRow({ offer }) {
  const listing = offer.listing;
  const studio = offer.studio;

  const listPrice =
    typeof listing?.price_cents === "number"
      ? formatPrice(listing.price_cents, listing.currency || "AUD")
      : null;

  const offerPrice =
    typeof offer.amount_cents === "number"
      ? formatPrice(offer.amount_cents, offer.currency || "AUD")
      : `${offer.amount} ${offer.currency || "AUD"}`;

  const statusColor =
    offer.status === "accepted"
      ? "text-green-400 bg-green-500/10 border-green-500/40"
      : offer.status === "declined"
      ? "text-red-400 bg-red-500/10 border-red-500/40"
      : "text-amber-300 bg-amber-500/10 border-amber-500/40";

  return (
    <div className="flex gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
      <div className="w-20 h-20 rounded-lg overflow-hidden bg-black/30 flex-shrink-0">
        {listing?.main_image_url ? (
          <img
            src={listing.main_image_url}
            alt={listing?.title || "Listing"}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-8 h-8 text-white/30" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">
              {listing?.title || "Listing"}
            </p>
            {studio && (
              <p className="text-xs text-white/60 truncate mt-0.5 flex items-center gap-1">
                <span className="font-medium text-green-300">
                  {studio.display_name}
                </span>
                {studio.city && (
                  <>
                    <span className="text-white/40">•</span>
                    <MapPin className="w-3 h-3" />
                    <span>
                      {studio.city}
                      {studio.country ? `, ${studio.country}` : ""}
                    </span>
                  </>
                )}
              </p>
            )}
          </div>

          <span
            className={`px-2 py-0.5 rounded-full text-[11px] border ${statusColor}`}
          >
            {offer.status || "pending"}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-white/70">
          <div className="inline-flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            <span className="font-semibold">{offerPrice}</span>
            {listPrice && (
              <span className="text-white/40 text-[11px]">
                (listing {listPrice})
              </span>
            )}
          </div>

          <div className="inline-flex items-center gap-1 text-white/50">
            <Clock className="w-3 h-3" />
            <span>
              {offer.created_at
                ? new Date(offer.created_at).toLocaleString()
                : ""}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
